import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Chart from '../components/Chart';
import MetricCard from '../components/MetricCard';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiUsers, FiClock, FiTrendingDown, FiPhone, FiMail, FiMessageSquare } = FiIcons;

const RetentionRisk = () => {
  const [riskLevel, setRiskLevel] = useState('all');

  const riskMetrics = [
    {
      title: 'High Risk Customers',
      value: '23',
      change: '15%',
      changeType: 'negative',
      icon: FiAlertTriangle,
      color: 'danger',
      subtitle: 'Immediate action needed'
    },
    {
      title: 'Medium Risk',
      value: '45',
      change: '8%',
      changeType: 'positive',
      icon: FiUsers,
      color: 'warning',
      subtitle: 'Monitor closely'
    },
    {
      title: 'Avg. Time to Churn',
      value: '14 days',
      change: '3 days',
      changeType: 'negative',
      icon: FiClock,
      color: 'secondary',
      subtitle: 'From first negative signal'
    },
    {
      title: 'Retention Rate',
      value: '87%',
      change: '2%',
      changeType: 'negative',
      icon: FiTrendingDown,
      color: 'primary',
      subtitle: 'Monthly retention'
    }
  ];

  const riskTrendsData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    series: [
      {
        name: 'High Risk',
        type: 'line',
        data: [15, 18, 22, 25, 28, 23],
        smooth: true,
        lineStyle: { color: '#ef4444' },
        areaStyle: { color: 'rgba(239, 68, 68, 0.1)' }
      },
      {
        name: 'Medium Risk',
        type: 'line',
        data: [35, 38, 42, 45, 48, 45],
        smooth: true,
        lineStyle: { color: '#f59e0b' },
        areaStyle: { color: 'rgba(245, 158, 11, 0.1)' }
      },
      {
        name: 'Low Risk',
        type: 'line',
        data: [150, 144, 138, 132, 124, 132],
        smooth: true,
        lineStyle: { color: '#22c55e' },
        areaStyle: { color: 'rgba(34, 197, 94, 0.1)' }
      }
    ]
  };

  const riskFactorsData = {
    labels: ['Support Tickets', 'Low Usage', 'Payment Issues', 'Negative Feedback', 'No Engagement'],
    series: [
      {
        name: 'Risk Factors',
        type: 'bar',
        data: [
          { value: 45, itemStyle: { color: '#ef4444' } },
          { value: 38, itemStyle: { color: '#f59e0b' } },
          { value: 32, itemStyle: { color: '#0ea5e9' } },
          { value: 28, itemStyle: { color: '#8b5cf6' } },
          { value: 22, itemStyle: { color: '#64748b' } }
        ]
      }
    ]
  };

  const highRiskCustomers = [
    {
      name: 'Acme Corp',
      email: 'contact@acmecorp.com',
      riskScore: 85,
      lastActivity: '7 days ago',
      issues: ['Multiple support tickets', 'Low product usage', 'Billing concerns'],
      value: '$12,500',
      contactHistory: [
        { type: 'email', date: '2 days ago', status: 'sent' },
        { type: 'call', date: '5 days ago', status: 'completed' }
      ]
    },
    {
      name: 'TechStart Solutions',
      email: 'team@techstart.io',
      riskScore: 78,
      lastActivity: '12 days ago',
      issues: ['No recent logins', 'Negative sentiment', 'Feature complaints'],
      value: '$8,900',
      contactHistory: [
        { type: 'email', date: '1 day ago', status: 'pending' }
      ]
    },
    {
      name: 'Global Industries',
      email: 'admin@globalind.com',
      riskScore: 72,
      lastActivity: '5 days ago',
      issues: ['Support escalation', 'Integration problems'],
      value: '$15,600',
      contactHistory: [
        { type: 'call', date: '3 days ago', status: 'scheduled' }
      ]
    }
  ];

  const retentionActions = [
    {
      title: 'Personalized Outreach',
      description: 'Send targeted emails based on customer behavior and risk factors',
      effectiveness: '73%',
      avgTime: '2 hours'
    },
    {
      title: 'Customer Success Call',
      description: 'Schedule 1-on-1 calls with high-risk customers to address concerns',
      effectiveness: '68%',
      avgTime: '1 day'
    },
    {
      title: 'Feature Training',
      description: 'Provide additional training on underutilized features',
      effectiveness: '61%',
      avgTime: '3 days'
    },
    {
      title: 'Discount Offers',
      description: 'Strategic pricing incentives for at-risk customers',
      effectiveness: '45%',
      avgTime: '1 hour'
    }
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Customer Retention Risk</h1>
          <p className="text-secondary-600 mt-1">Identify and engage at-risk customers before they churn</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <select 
            value={riskLevel}
            onChange={(e) => setRiskLevel(e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Risk Levels</option>
            <option value="high">High Risk</option>
            <option value="medium">Medium Risk</option>
            <option value="low">Low Risk</option>
          </select>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2 bg-danger-500 text-white rounded-lg hover:bg-danger-600 transition-colors font-medium"
          >
            Export Risk List
          </motion.button>
        </div>
      </div>

      {/* Risk Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {riskMetrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Chart 
          title="Risk Level Trends"
          data={riskTrendsData}
          type="line"
          height={400}
        />
        
        <Chart 
          title="Top Risk Factors"
          data={riskFactorsData}
          type="bar"
          height={400}
        />
      </div>

      {/* High Risk Customers */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-secondary-900">High Risk Customers</h3>
          <button className="text-danger-600 hover:text-danger-700 font-medium text-sm">
            View All ({highRiskCustomers.length})
          </button>
        </div>
        
        <div className="space-y-4">
          {highRiskCustomers.map((customer, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 border border-danger-200 rounded-lg bg-danger-50"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-secondary-900">{customer.name}</h4>
                  <p className="text-sm text-secondary-600">{customer.email}</p>
                  <p className="text-xs text-secondary-500 mt-1">Last activity: {customer.lastActivity}</p>
                </div>
                
                <div className="text-right">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-danger-700">Risk Score:</span>
                    <span className="text-lg font-bold text-danger-600">{customer.riskScore}</span>
                  </div>
                  <p className="text-sm text-secondary-700">Value: {customer.value}</p>
                </div>
              </div>
              
              <div className="mb-4">
                <h5 className="text-sm font-medium text-secondary-900 mb-2">Issues:</h5>
                <div className="flex flex-wrap gap-2">
                  {customer.issues.map((issue, issueIndex) => (
                    <span 
                      key={issueIndex}
                      className="px-2 py-1 bg-danger-200 text-danger-800 text-xs rounded-full"
                    >
                      {issue}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-medium text-secondary-900 mb-2">Recent Contact:</h5>
                  <div className="flex items-center space-x-3">
                    {customer.contactHistory.map((contact, contactIndex) => (
                      <div key={contactIndex} className="flex items-center space-x-1">
                        {contact.type === 'email' && <FiMail className="w-3 h-3 text-secondary-500" />}
                        {contact.type === 'call' && <FiPhone className="w-3 h-3 text-secondary-500" />}
                        <span className="text-xs text-secondary-600">{contact.date}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          contact.status === 'completed' ? 'bg-success-100 text-success-700' :
                          contact.status === 'pending' ? 'bg-warning-100 text-warning-700' :
                          'bg-primary-100 text-primary-700'
                        }`}>
                          {contact.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-1 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 transition-colors">
                    Schedule Call
                  </button>
                  <button className="px-3 py-1 bg-secondary-500 text-white text-sm rounded-lg hover:bg-secondary-600 transition-colors">
                    Send Email
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Retention Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-6">Recommended Retention Actions</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {retentionActions.map((action, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 border border-secondary-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-medium text-secondary-900">{action.title}</h4>
                <div className="text-right">
                  <p className="text-sm font-medium text-success-600">{action.effectiveness}</p>
                  <p className="text-xs text-secondary-500">success rate</p>
                </div>
              </div>
              
              <p className="text-sm text-secondary-700 mb-3">{action.description}</p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-secondary-500">Avg. time: {action.avgTime}</span>
                <button className="px-3 py-1 bg-primary-50 text-primary-600 text-sm rounded-lg hover:bg-primary-100 transition-colors">
                  Execute
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RetentionRisk;